import type { Layer, TagsManager } from 'dxf-writer'
import DatabaseObject from 'dxf-writer/src/DatabaseObject.js'
import Block from './Block'
import type Drawing from './Drawing'
import MText from './MText'
import Polyline from './Polyline'

/**
 * 将字符串中的非 ASCII 字符转换为 DXF MTEXT 标准 Unicode 转义序列 \U+XXXX。
 * 此方式兼容所有支持 DXF R2000+ 的 CAD 软件，与文件编码无关。
 */
function toMTextUnicode(str: string): string {
  if (!str) return ''
  return Array.from(str, (char) => {
    const codePoint = char.charCodeAt(0)
    return codePoint <= 0x7f ? char : `\\U+${codePoint.toString(16).toUpperCase().padStart(4, '0')}`
  }).join('')
}

/**
 * 按估算字符宽度手动分行，防止 DXF 查看器不支持 MTEXT 自动换行时文字溢出边框。
 * - ASCII 字符宽度 ≈ charHeight × 0.9（含字间距，保守估算）
 * - 非 ASCII（中文等全角）字符宽度 ≈ charHeight × 1.0
 * 逐字累加，超出 maxWidth 时强制换行。
 */
function wrapMText(text: string, maxWidth: number, charHeight: number): string {
  if (!text) return text
  const getCharWidth = (ch: string) => (ch.charCodeAt(0) > 0x7f ? charHeight : charHeight * 0.9)

  const lines: string[] = []
  let line = ''
  let lineWidth = 0

  for (const ch of text) {
    const cw = getCharWidth(ch)
    if (lineWidth + cw > maxWidth && line.length > 0) {
      lines.push(line)
      line = ch
      lineWidth = cw
    } else {
      line += ch
      lineWidth += cw
    }
  }
  if (line) lines.push(line)
  return lines.join('\n')
}

/** 图框纸张尺寸表，单位毫米，数组为 [宽, 高]（横向） */
// 纸张尺寸（mm）：[宽, 高]（横向）
export const FRAME_PAPER_SIZES: Record<string, [number, number]> = {
  A1: [840, 594],
  A2: [594, 420],
  A3: [420, 297],
  A4: [297, 210],
}

/**
 * DXF 图框构建选项。
 */
export interface DxfFrameBuilderOptions {
  /** 纸张大小 */
  paperSize: 'A1' | 'A2' | 'A3' | 'A4'
  /** 出图比例分母，如 100 代表 1:100 */
  scale: number
  /** 产品名称 */
  productName: string
  /** 项目名称 */
  projectName: string
  /** 制图单位 */
  drawingUnit: string
  /** 图名 */
  drawingName: string
  /** 图号 */
  drawingNumber: string
  /** 日期 */
  date: string
  /** logo 图片路径（相对路径），需与 DXF 文件同目录 */
  logoPath?: string
  /** logo 图片像素宽 */
  logoPixelWidth?: number
  /** logo 图片像素高 */
  logoPixelHeight?: number
  /** 底图信息 */
  underlayImage?: {
    path: string
    width: number
    height: number
    pointCloudBBox?: {
      minX: number
      minY: number
      minZ: number
      maxX: number
      maxY: number
      maxZ: number
    }
  }
}

/** 图框图片构建产物 */
export interface FrameImageBuildArtifacts {
  /** 插入点 X（相对于 BLOCK 原点） */
  x: number
  /** 插入点 Y（相对于 BLOCK 原点） */
  y: number
  /** 显示宽度（模型单位） */
  width: number
  /** 显示高度（模型单位） */
  height: number
  /** 图片资源路径（相对 DXF 文件） */
  logoPath: string
  /** 图片像素宽度 */
  pixelWidth: number
  /** 图片像素高度 */
  pixelHeight: number
  /** 承载图片的图层名 */
  frameLayerName: string
  /** 承载图片的块名，缺省表示直接插入 model space */
  frameBlockName?: string
  /** 是否直接写入 model space */
  insertToModelSpace?: boolean
  /** 是否插入到 model space 实体序列的首位 */
  insertAtStart?: boolean
  /** 附加选项 */
  options?: {
    /** 淡入度值0-100 */
    fadeInValue?: number
  }
}

// ─── INSERT 实体（块参照）────────────────────────────────────────────────────

class Insert extends DatabaseObject {
  layer!: Layer

  constructor(
    public blockName: string,
    public x: number,
    public y: number,
    public xScale = 1,
    public yScale = 1,
    public rotation = 0
  ) {
    super(['AcDbEntity', 'AcDbBlockReference'])
  }

  tags(manager: TagsManager) {
    manager.push(0, 'INSERT')
    super.tags(manager)
    manager.push(8, this.layer.name)
    manager.push(2, this.blockName)
    manager.push(10, this.x)
    manager.push(20, this.y)
    manager.push(30, 0)
    manager.push(41, this.xScale)
    manager.push(42, this.yScale)
    manager.push(43, 1)
    manager.push(50, this.rotation)
  }
}

// ─── BLOCK_RECORD 条目 ────────────────────────────────────────────────────────

class BlockRecord extends DatabaseObject {
  constructor(public name: string) {
    super(['AcDbSymbolTableRecord', 'AcDbBlockTableRecord'])
  }

  tags(manager: TagsManager) {
    manager.push(0, 'BLOCK_RECORD')
    super.tags(manager)
    manager.push(2, this.name)
    manager.push(340, 0)
    manager.push(310, '')
    manager.push(310, '')
  }
}

// ─── DxfFrameBuilder ─────────────────────────────────────────────────────────

/**
 * DXF 图框生成器，负责在 Drawing 中构造 FRAME 块及其标题栏内容。
 */
export class DxfFrameBuilder {
  /**
   * 在 drawing 中创建 BLOCK "FRAME"，包含外边框、内边框、标题栏线条和文字。
   * 同时向 model space 写入 INSERT 实体，将图框定位到 frameOrigin。
   *
   * @param drawing - 目标 Drawing 实例
   * @param options - 图框构建选项
   * @param frameOrigin - 图框插入原点（模型坐标）
   * @returns logo 区域信息（BLOCK 内坐标），供后续注册 IMAGE 相关对象使用；
   *          若 logoPath 为空则返回 null。
   */
  static generate(
    drawing: Drawing,
    options: DxfFrameBuilderOptions,
    frameOrigin: { x: number; y: number },
    coordinateScale: number = options.scale
  ): FrameImageBuildArtifacts | null {
    const {
      paperSize,
      scale: drawingScale,
      productName,
      projectName,
      drawingUnit,
      drawingName,
      drawingNumber,
      date,
      logoPath = './SHARE3DCAM_logo.png',
      logoPixelWidth = 1080,
      logoPixelHeight = 1080,
    } = options

    const [pw, ph] = FRAME_PAPER_SIZES[paperSize] ?? [420, 297]
    const W = pw * coordinateScale
    const H = ph * coordinateScale

    // ── 添加图层 ──────────────────────────────────────────────────────────
    const FRAME_LAYER = '图框'
    const TITLE_LAYER = '标题栏'

    if (!(drawing as any).layers[FRAME_LAYER]) {
      drawing.addLayer(FRAME_LAYER, 4, 'CONTINUOUS')
    }
    if (!(drawing as any).layers[TITLE_LAYER]) {
      drawing.addLayer(TITLE_LAYER, 7, 'CONTINUOUS')
    }

    const frameLayer = (drawing as any).layers[FRAME_LAYER] as Layer
    const titleLayer = (drawing as any).layers[TITLE_LAYER] as Layer

    // ── 创建 BLOCK "FRAME" ────────────────────────────────────────────────
    const block = new Block('FRAME', 0)
    ;(drawing as any).blocks['FRAME'] = block

    // 注册 BLOCK_RECORD（DXF R2000+ 合规）
    ;(drawing as any).tables?.['BLOCK_RECORD']?.add(new BlockRecord('FRAME'))

    // 向 BLOCK 内添加图形的辅助函数
    const add = (layer: Layer, shape: any) => {
      shape.layer = layer
      block.addShape(shape)
    }

    // ── 外边框 ────────────────────────────────────────────────────────────
    const outer = new Polyline(
      [
        [0, 0],
        [W, 0],
        [W, H],
        [0, H],
      ],
      true
    )
    ;(outer as any).lineWidth = 50 // 0.5 mm
    add(frameLayer, outer)

    // ── 内边框（左侧装订边 25mm，其余 5mm）────────────────────────────────
    const ml = 25 * coordinateScale
    const m = 5 * coordinateScale
    const ix1 = ml,
      iy1 = m,
      ix2 = W - m,
      iy2 = H - m
    // ── 内边框在图纸上的线宽（1mm）────────────────────────────────
    const innerBorderWidth = 1 * coordinateScale

    const inner = new Polyline(
      [
        [ix1, iy1],
        [ix2, iy1],
        [ix2, iy2],
        [ix1, iy2],
      ],
      true,
      innerBorderWidth,
      innerBorderWidth
    )
    ;(inner as any).lineWidth = 50
    add(frameLayer, inner)

    // ── 标题栏（右侧整栏，按 A3@1:100 示例尺寸）───────────────────────────
    const tbWidth = 50 * coordinateScale
    const logoRowH = 50 * coordinateScale
    const companyRowH = 10 * coordinateScale

    const labelRows = [
      '项目名称：',
      '制图单位：',
      '图名：',
      '图号：',
      '图纸比例：',
      '图纸大小：',
      '日期：',
    ]
    const valueRows = [
      projectName,
      drawingUnit,
      drawingName,
      drawingNumber,
      `1:${drawingScale}`,
      paperSize,
      date,
    ]
    const numRows = labelRows.length
    // 信息区行高：前两行 2000，其余 1000（单位：模型单位）
    const baseInfoRowHeights = [20, 20, 10, 10, 10, 10, 10].map((mm) => mm * coordinateScale)
    const baseInfoHeight = baseInfoRowHeights.reduce((sum, h) => sum + h, 0)

    const tbX1 = ix2 - tbWidth
    const tbX2 = ix2
    const tbY1 = iy1
    const tbY2 = iy2

    // 在不同纸张尺寸下，若可用高度不足则按比例压缩信息区，确保布局始终有效
    const innerHeight = tbY2 - tbY1
    const availableInfoHeight = Math.max(0, innerHeight - logoRowH - companyRowH)
    const infoScale =
      availableInfoHeight < baseInfoHeight && baseInfoHeight > 0
        ? availableInfoHeight / baseInfoHeight
        : 1
    const infoRowHeights = baseInfoRowHeights.map((h) => h * infoScale)
    const infoHeight = infoRowHeights.reduce((sum, h) => sum + h, 0)
    const middleBlankH = Math.max(0, innerHeight - logoRowH - companyRowH - infoHeight)
    const logoBottomY = tbY2 - logoRowH
    const companyBottomY = logoBottomY - companyRowH
    const infoTopY = companyBottomY - middleBlankH

    // 标题栏外框
    const tbBorder = new Polyline(
      [
        [tbX1, tbY1],
        [tbX2, tbY1],
        [tbX2, tbY2],
        [tbX1, tbY2],
      ],
      true
    )
    ;(tbBorder as any).lineWidth = 35
    add(frameLayer, tbBorder)

    // 标题栏左侧分隔竖线（延伸至内边框顶部，形成绘图区与标题栏的分界）
    const vSep = new Polyline(
      [
        [tbX1, iy1],
        [tbX1, iy2],
      ],
      false
    )
    ;(vSep as any).lineWidth = 35
    add(frameLayer, vSep)

    // logo 区底线
    const logoSep = new Polyline(
      [
        [tbX1, logoBottomY],
        [tbX2, logoBottomY],
      ],
      false
    )
    ;(logoSep as any).lineWidth = 25
    add(frameLayer, logoSep)

    // 公司名行底线
    const companySep = new Polyline(
      [
        [tbX1, companyBottomY],
        [tbX2, companyBottomY],
      ],
      false
    )
    ;(companySep as any).lineWidth = 15
    add(frameLayer, companySep)

    // 信息表顶部线
    const infoTopSep = new Polyline(
      [
        [tbX1, infoTopY],
        [tbX2, infoTopY],
      ],
      false
    )
    ;(infoTopSep as any).lineWidth = 15
    add(frameLayer, infoTopSep)

    // 标签列 / 内容列 竖向分隔线
    const colX = tbX1 + tbWidth * 0.4
    const colSep = new Polyline(
      [
        [colX, tbY1],
        [colX, infoTopY],
      ],
      false
    )
    ;(colSep as any).lineWidth = 15
    add(frameLayer, colSep)

    // 公司名（logo 区下方单独一行，居中）
    const companyTextH = 4.5 * coordinateScale
    const companyTextY = (logoBottomY + companyBottomY) / 2
    const companyText = new MText(
      (tbX1 + tbX2) / 2,
      companyTextY,
      companyTextH,
      0,
      toMTextUnicode(productName),
      tbWidth,
      5
    )
    add(titleLayer, companyText)

    // 信息行（自上而下按示例行高布局）
    const textH = 3 * coordinateScale
    const labelColWidth = colX - tbX1
    const valueColWidth = tbX2 - colX
    let rowTop = infoTopY
    for (let i = 0; i < numRows; i++) {
      const rowHeight =
        infoRowHeights[i] ?? infoRowHeights[infoRowHeights.length - 1] ?? 10 * coordinateScale
      const rowBottom = rowTop - rowHeight
      const rowMidY = (rowTop + rowBottom) / 2

      // 行分隔线（底部边界与 tbBorder 重合，不重复绘制）
      if (i < numRows - 1) {
        const rowLine = new Polyline(
          [
            [tbX1, rowBottom],
            [tbX2, rowBottom],
          ],
          false
        )
        ;(rowLine as any).lineWidth = 15
        add(frameLayer, rowLine)
      }

      // 标签文字（左对齐）—— 使用 MTEXT + \U+XXXX 保证中文正确显示
      const labelPadding = coordinateScale
      const labelX = tbX1 + labelPadding
      const labelTxt = new MText(
        labelX,
        rowMidY,
        textH,
        0,
        toMTextUnicode(labelRows[i]),
        labelColWidth - labelPadding,
        4
      )
      add(titleLayer, labelTxt)

      // 内容文字（左对齐）—— 插入点紧贴分隔线右侧，先手动分行避免文字超出右边框
      const valuePadding = coordinateScale
      const maxValueWidth = valueColWidth - valuePadding
      const wrappedValue = wrapMText(valueRows[i], maxValueWidth, textH)
      const valueTxt = new MText(
        colX + valuePadding,
        rowMidY,
        textH,
        0,
        toMTextUnicode(wrappedValue),
        maxValueWidth,
        4
      )
      add(titleLayer, valueTxt)

      rowTop = rowBottom
    }

    // ── 向 model space 插入 FRAME 块 ─────────────────────────────────────
    drawing.setActiveLayer(FRAME_LAYER)
    const insert = new Insert('FRAME', frameOrigin.x, frameOrigin.y)
    drawing.activeLayer?.addShape(insert as any)

    if (!logoPath) {
      return null
    }

    // 返回 logo 区域（BLOCK 内相对坐标）
    return {
      x: tbX1,
      y: logoBottomY,
      width: tbWidth,
      height: logoRowH,
      logoPath,
      pixelWidth: logoPixelWidth,
      pixelHeight: logoPixelHeight,
      frameLayerName: FRAME_LAYER,
      frameBlockName: 'FRAME',
    }
  }

  /**
   * 构建点云附底图片产物。
   *
   * @param options - 图框构建选项，读取其中的 underlayImage
   * @param modelScale - 模型空间缩放倍数
   * @returns 附底图片构建产物，未配置或尺寸非法时返回 null
   */
  static buildUnderlayImageArtifacts(
    options: DxfFrameBuilderOptions,
    modelScale: number
  ): FrameImageBuildArtifacts | null {
    const underlayImage = options.underlayImage
    const bbox = underlayImage?.pointCloudBBox

    if (!underlayImage?.path || !bbox) {
      return null
    }

    const width = (bbox.maxX - bbox.minX) * modelScale
    const height = (bbox.maxY - bbox.minY) * modelScale
    if (width <= 0 || height <= 0) {
      return null
    }

    return {
      x: bbox.minX * modelScale,
      y: bbox.minY * modelScale,
      width,
      height,
      logoPath: underlayImage.path,
      pixelWidth: underlayImage.width,
      pixelHeight: underlayImage.height,
      frameLayerName: '点云附底',
      insertToModelSpace: true,
      insertAtStart: true,
      options: {
        fadeInValue: 50,
      },
    }
  }
}
